import Fuse from 'fuse.js'

export type BuildingTypeSearchOption = {
  usageType: string
  usageName: string
  id: string
  name: string
}

type IndexedBuildingTypeSearchOption<T extends BuildingTypeSearchOption> = T & {
  searchTerms: string
  tokenBag: string
}

function collapseWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

function sanitizeSearchText(value: string): string {
  return collapseWhitespace(value.replace(/[^\p{L}\p{N}]+/gu, ' '))
}

function normalizeGermanSearchText(value: string): string {
  return sanitizeSearchText(
    value
      .toLocaleLowerCase('de-DE')
      .replace(/ß/g, 'ss')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .normalize('NFKD')
      .replace(/\p{Diacritic}/gu, '')
  )
}

function tokenizeSearchText(value: string): string[] {
  return normalizeGermanSearchText(value)
    .split(' ')
    .map((token) => token.trim())
    .filter(Boolean)
}

function buildTokenBag(...values: string[]): string {
  const tokens = new Set<string>()

  for (const value of values) {
    for (const token of tokenizeSearchText(value)) {
      tokens.add(token)
    }
  }

  return Array.from(tokens).sort().join(' ')
}

function buildSearchTerms(...values: string[]): string {
  const terms = new Set<string>()

  for (const value of values) {
    const lowerValue = sanitizeSearchText(value.toLocaleLowerCase('de-DE'))
    const normalizedValue = normalizeGermanSearchText(value)

    if (lowerValue) {
      terms.add(lowerValue)
      terms.add(lowerValue.replace(/\s+/g, ''))
    }

    if (normalizedValue) {
      terms.add(normalizedValue)
      terms.add(normalizedValue.replace(/\s+/g, ''))
    }
  }

  return Array.from(terms).join(' ')
}

function buildQueryVariants(query: string): string[] {
  const variants = new Set<string>()
  const trimmedQuery = collapseWhitespace(query)
  const normalizedQuery = normalizeGermanSearchText(query)
  const tokenBag = buildTokenBag(query)

  if (trimmedQuery) variants.add(trimmedQuery)
  if (normalizedQuery) variants.add(normalizedQuery)
  if (tokenBag) variants.add(tokenBag)

  return Array.from(variants)
}

function sortBuildingTypes<T extends BuildingTypeSearchOption>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name) || a.usageName.localeCompare(b.usageName))
}

export function searchBuildingTypes<T extends BuildingTypeSearchOption>(
  items: T[],
  query: string,
  threshold = 0.28
): T[] {
  const trimmedQuery = collapseWhitespace(query)
  if (!trimmedQuery) {
    return sortBuildingTypes(items)
  }

  const indexedItems: IndexedBuildingTypeSearchOption<T>[] = items.map((item) => ({
    ...item,
    searchTerms: buildSearchTerms(item.name, item.usageName, `${item.usageName} ${item.name}`),
    tokenBag: buildTokenBag(item.name, item.usageName, `${item.usageName} ${item.name}`),
  }))

  const fuse = new Fuse(indexedItems, {
    ignoreLocation: true,
    includeScore: true,
    minMatchCharLength: 2,
    shouldSort: true,
    threshold,
    keys: [
      { name: 'name', weight: 0.5 },
      { name: 'searchTerms', weight: 0.25 },
      { name: 'tokenBag', weight: 0.15 },
      { name: 'usageName', weight: 0.1 },
    ],
  })

  const bestResults = new Map<string, { item: T; score: number }>()

  for (const variant of buildQueryVariants(trimmedQuery)) {
    for (const result of fuse.search(variant)) {
      const existing = bestResults.get(result.item.id)
      const score = result.score ?? Number.POSITIVE_INFINITY
      if (!existing || score < existing.score) {
        bestResults.set(result.item.id, { item: result.item, score })
      }
    }
  }

  return Array.from(bestResults.values())
    .sort((a, b) => {
      if (a.score !== b.score) {
        return a.score - b.score
      }

      return a.item.name.localeCompare(b.item.name) || a.item.usageName.localeCompare(b.item.usageName)
    })
    .map((result) => result.item)
}
